import { useCallback, useState, useImperativeHandle, forwardRef } from "react";
import { useDropzone } from "react-dropzone";
import { FaFileUpload } from "react-icons/fa";
import { LuUpload } from "react-icons/lu";
import * as XLSX from 'xlsx';
import JSZip from 'jszip';

const UploadFile = forwardRef(({ setProcessedData }, ref) => {
    const [dragActive, setDragActive] = useState(false);
    const [uploaded, setUploaded] = useState(false);
    const [error, setError] = useState(false);

    // Expõe a função reset para o componente pai
    useImperativeHandle(ref, () => ({
        reset: () => {
            setUploaded(false);
            setError(false);
            setDragActive(false);
            setProcessedData([]);
        }
    }));

    const parseHtmlToObject = (htmlString) => {
        const parser = new DOMParser();
        const doc = parser.parseFromString(htmlString, 'text/html');
        const rows = doc.querySelectorAll('tr');
        const dataArray = [];

        rows.forEach(row => {
            const cells = row.querySelectorAll('td');
            const rowData = [];
            cells.forEach(cell => rowData.push(cell.textContent.trim()));
            dataArray.push(rowData);
        });

        try {
            const formatHeader = (text) => {
                if (!text || typeof text !== "string") return '';
                const normalized = text
                    .normalize('NFD')
                    .replace(/[\u0300-\u036f]/g, '')
                    .replace(/[^\w\s]/gi, '')
                    .toLowerCase()
                    .split(' ')
                    .filter(Boolean);

                return normalized
                    .map((word, index) => index === 0 ? word : word.charAt(0).toUpperCase() + word.slice(1))
                    .join('');
            };

            const headers = dataArray[0].map(header => formatHeader(header));

            const result = dataArray.slice(1).map(row => {
                const rowObj = {};
                headers.forEach((key, index) => {
                    rowObj[key] = row[index];
                });
                return rowObj;
            });

            setError(false);
            return result;
        } catch {
            setError(true);
            return null;
        }
    };

    const handleZipFile = async (data) => {
        const zip = new JSZip();
        try {
            const content = await zip.loadAsync(data);
            const files = Object.keys(content.files);
            const xlsxFile = files.find(name => name.endsWith('.xlsx') || name.endsWith('.xls'));
            if (xlsxFile) {
                const extracted = await content.files[xlsxFile].async('arraybuffer');
                processExcelFile(new Uint8Array(extracted));
            } else {
                setError(true);
                setProcessedData([]);
            }
        } catch (err) {
            setError(true);
            setProcessedData([]);
            console.error(err);
        }
    };

    const processExcelFile = (data) => {
        try {
            const workbook = XLSX.read(data, { type: 'array' });
            const worksheet = workbook.Sheets[workbook.SheetNames[0]];
            const json = XLSX.utils.sheet_to_json(worksheet, { header: 1 });
            const htmlString = json[json.length - 1][4];
            const result = parseHtmlToObject(htmlString);

            if (!result) {
                setError(true);
                setProcessedData([]);
                return;
            }

            setProcessedData(result);
            setUploaded(true);
            setError(false);
        } catch (err) {
            setError(true);
            setProcessedData([]);
            console.error('Erro ao processar Excel:', err);
        }
    };

    const handleDrop = (file) => {
        if (uploaded || error) return;

        const fileExtension = file.name.split('.').pop().toLowerCase();
        const reader = new FileReader();

        reader.onload = (e) => {
            const data = new Uint8Array(e.target.result);

            if (fileExtension === 'zip') {
                handleZipFile(data);
            } else if (fileExtension === 'xlsx' || fileExtension === 'xls') {
                processExcelFile(data);
            } else {
                setError(true);
                setProcessedData([]);
            }
        };

        reader.readAsArrayBuffer(file);
    };

    const onDrop = useCallback((acceptedFiles) => {
        if (uploaded) return;
        acceptedFiles.forEach(file => handleDrop(file));
    }, [uploaded]);

    const { getRootProps, getInputProps } = useDropzone({
        onDrop,
        accept: {
            'application/zip': ['.zip']
        },
        noClick: uploaded,
        noDrag: uploaded,
        disabled: uploaded,
        onDragEnter: () => {
            if (!uploaded) setDragActive(true);
        },
        onDragLeave: () => {
            if (!uploaded) setDragActive(false);
        },
        onDropAccepted: () => setDragActive(false),
    });

    return (
        <div className="itenUpload">
            <span>AWB / Data do Último Status / Descrição do status</span>
            <div
                {...getRootProps()}
                className={`dropzone ${dragActive ? 'active' : ''} ${uploaded ? 'disabled' : ''} ${error ? 'error' : ''}`}
            >
                <input {...getInputProps()} />
                <div className="upload-icon">
                    {uploaded ? <FaFileUpload style={{ color: 'green' }} /> : 
                     error ? <FaFileUpload style={{ color: 'red' }} /> : 
                     dragActive ? <FaFileUpload /> : <LuUpload />}
                </div>
                {error && <p style={{ color: 'red', fontSize: '12px', marginTop: '8px' }}>Erro ao processar arquivo</p>}
            </div>
        </div>
    );
});

export default UploadFile;